import { Modal } from '@/components/shared/Modal';
import { Button } from '@/components/ui/button';

/**
 * Suggests — never performs — a Ship-To change after the received location moves.
 *
 * THE RULE THIS ENFORCES: the system may suggest an address change, it may
 * never make one. Return Location silently following Received From is the
 * production bug `received-from-wiring.ts` was written to document; doing it
 * automatically here would reintroduce it through a different door.
 *
 * Every exit except the explicit confirm means KEEP CURRENT:
 *   - the default focused action is Keep current
 *   - Escape closes via the Modal's own handler, which calls onKeepCurrent
 *   - clicking the backdrop does the same
 * Nothing is written unless "Use suggested location" is clicked, and even then
 * only Ship-To moves — Bill-To is never included in the request, because
 * billing is agreed with the customer, not with the loading bay.
 */
export function ReceivedLocationChangePrompt({
  open,
  currentShipToLabel,
  suggestedLocationLabel,
  onKeepCurrent,
  onUseSuggested,
  saving,
}: {
  open: boolean;
  currentShipToLabel: string;
  suggestedLocationLabel: string;
  onKeepCurrent: () => void;
  onUseSuggested: () => void;
  saving?: boolean;
}) {
  return (
    <Modal open={open} title="Received location changed" onClose={onKeepCurrent} size="md">
      <div className="space-y-4">
        <p className="text-sm text-foreground">Received location changed. Update Ship-To?</p>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="rounded-lg border border-border bg-muted/20 px-3 py-2">
            <p className="text-xs text-muted-foreground">Current Ship-To</p>
            <p className="mt-0.5 text-sm text-foreground">{currentShipToLabel || '—'}</p>
          </div>
          <div className="rounded-lg border border-primary/40 bg-primary/5 px-3 py-2">
            <p className="text-xs text-muted-foreground">Suggested</p>
            <p className="mt-0.5 text-sm text-foreground">{suggestedLocationLabel || '—'}</p>
          </div>
        </div>

        <p className="text-xs italic text-muted-foreground">Bill-To will not be changed.</p>

        <div className="flex justify-end gap-2 pt-1">
          {/* Keep current is first and is the default action. Doing nothing is
              the safe outcome, so it must also be the easy one. */}
          <Button variant="secondary" onClick={onKeepCurrent} disabled={saving} autoFocus>
            Keep current
          </Button>
          <Button onClick={onUseSuggested} disabled={saving}>
            {saving ? 'Updating…' : 'Use suggested location'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
